import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from '@blocs.i18n';
import { ButtonAsWrapper, Dialog, FlexCol, FlexRow } from '@minecraft.atoms';
import Icon, { IconProps } from '@minecraft.icon';
import Tooltip from '@minecraft.uncontrolled-tooltip';
import { useClickOutside } from '@minecraft.hooks';
import { SelectionTagButton, SelectionTagButtonProps } from './SelectionTagButton';
import { SelectionTagType, TileValue } from './types';
import {
  getSelectionTileValuesToUse,
  validateSelectionButtonSize,
  validateSelectionTagType,
  useResponsiveSelectionTagProps,
  getToggleButtonAriaLabel,
} from './utils';

export type SelectionTagDialogProps<SchemaType extends SelectionTagType> = {
  /**
   * which type of selection tags to use, either 'number' or 'letter'
   * @default 'number'
   */
  type: SchemaType;
  /**
   * currently selected value
   * if type = 'number', it can be one of 1, 2, 3, 4, 5, 6, CB
   * if type = 'letter', it can be one of A, B, C, D, E, F, G
   * if no value is selected, it should be an empty string
   * @default ''
   */
  selectedValue: TileValue<SchemaType> | '';
  /**
   * data-qa-id for testing purposes
   */
  'data-qa-id'?: string;
  /** data-testid for testing purposes */
  'data-testid'?: string;
  /**
   * function to call when a new value is selected
   * @param newValue - the newly selected value
   * @returns a promise that should resolve to an object with a `success` property
   * indicating whether the selection was successful or not
   * if success is true, the dialog will close automatically
   * if success is false, the dialog will remain open
   */
  onSelected: (params: { value: TileValue<SchemaType> }) => Promise<{ success: boolean }>;
  /**
   * whether the selection tag dialog is disabled, this will prevent the dialog from opening
   * and / or the selection buttons from being clickable / firing the onSelected callback
   * if true, the dialog will not open and the button will not be clickable
   * @default false
   */
  disabled?: boolean;
  /**
   * size of the selection tag button, can be 'lg', 'sm'
   * see reference to size in the design system, note that the size prop for this component
   * does not match the same size of the SelectionTagButton component for the same value
   * https://www.figma.com/design/AiDAD1WpprgfbZI2XV6hU1/Casting-Networks-Design-System?node-id=10632-3850&m=dev
   * "lg" renders button at 2rem (32px) height
   * "sm" renders button at 1.5rem (24px) height
   * @default 'lg'
   */
  size?: 'lg' | 'sm';
  /** props for the button(s) at the md breakpoint, use this to set the button at a different size between desktop / mobile screens if needed */
  mdProps?: { size?: 'lg' | 'sm' };
};

export const SelectionTagDialog = <SchemaType extends SelectionTagType>({
  type,
  selectedValue = '',
  'data-qa-id': dataQaId = 'selection-tag-dialog',
  'data-testid': dataTestId = 'selection-tag-dialog',
  onSelected,
  disabled,
  size: buttonSizeProp,
  mdProps = {},
}: SelectionTagDialogProps<SchemaType>) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const { getResponsiveValues } = useResponsiveSelectionTagProps();
  const {
    tagsToUse: TAGS_TO_USE,
    toggleLabel,
    buttonSize,
    iconSize,
    hasValidSelectedTag,
    dialogLabel,
    tooltipLabel,
  } = useMemo(() => {
    const validType = validateSelectionTagType(type);
    const mergedProps = getResponsiveValues({ props: { size: buttonSizeProp }, mdProps });
    const buttonSize = validateSelectionButtonSize(mergedProps?.size);
    const tagsToUse = getSelectionTileValuesToUse(validType);

    return {
      tagsToUse,
      hasValidSelectedTag: tagsToUse.includes(selectedValue),
      buttonSize: (buttonSize === 'lg' ? 'xl' : 'lg') as SelectionTagButtonProps['size'],
      iconSize: (buttonSize === 'lg' ? 'lg' : 'md') as IconProps['size'],
      toggleLabel: getToggleButtonAriaLabel({
        type: validType,
        selectedValue,
        disabled: !!disabled,
        t,
      }),
      dialogLabel: type === 'letter' ? t('common:label.selectionTagsLetter') : t('common:label.selectionTagsNumber'),
      tooltipLabel:
        type === 'letter' ? t('common:selectionTagDialog.letterTooltip') : t('common:selectionTagDialog.numberTooltip'),
    };
  }, [type, t, getResponsiveValues, buttonSizeProp, mdProps, selectedValue, disabled]);
  const toggleDialog = () => setIsOpen(!isOpen);

  const handleSelectClick = useCallback(
    async (tag: TileValue<SchemaType>) => {
      const response = await onSelected({ value: tag });
      if (response?.success) {
        setIsOpen(false);
      }
    },
    [onSelected]
  );

  // if the dialog is open, clicking outside of it will close it
  useClickOutside({
    ref: wrapperRef,
    action: () => {
      if (isOpen) {
        setIsOpen(false);
      }
    },
  });

  return (
    <FlexRow ref={wrapperRef} data-qa-id={dataQaId} data-testid={dataTestId} grow={0} shrink={0}>
      {/* If there is a valid value selected, the toggle displays the selected value in a <SelectionTagButton />
       * If there is no valid value selected, the toggle displays an empty state with a plug <Icon /> toggle.
       */}
      {hasValidSelectedTag ? (
        <SelectionTagButton
          size={buttonSize}
          selected
          disabled={typeof onSelected !== 'function' || disabled}
          onSelected={() => toggleDialog()}
          tag={selectedValue}
          data-qa-id="selection-tag-dialog-toggle-btn"
          aria-label={toggleLabel}
          ref={toggleRef}
        />
      ) : (
        <FlexRow grow={0} shrink={0}>
          <ButtonAsWrapper
            ref={toggleRef}
            onClick={() => toggleDialog()}
            disabled={typeof onSelected !== 'function' || disabled}
            data-qa-id="selection-tag-dialog-toggle-btn"
            aria-label={toggleLabel}
            aria-selected={false}
          >
            <Icon name="add-1" themeColor="inactive" size={iconSize} />
          </ButtonAsWrapper>
          {!isOpen && (
            <Tooltip placement="top" target={toggleRef}>
              {tooltipLabel}
            </Tooltip>
          )}
        </FlexRow>
      )}
      <Dialog isOpen={isOpen} target={toggleRef} padding={2} aria-label={dialogLabel}>
        <FlexCol gap="2" wrap="nowrap">
          <FlexRow gap="2" wrap="nowrap" justifyContent="center" m="auto" data-testid="tags-row-1">
            {/*
             * The designs call for the 7 tags to be laid out in 2 rows
             * with the first four tags in the first row,
             * and the last three tags in the second row.
             * So here we're splitting the tags into two rows and rendering them accordingly.
             */}
            {TAGS_TO_USE.slice(0, 4).map((tag) => {
              const isSelected = selectedValue === tag;

              return (
                <SelectionTagButton
                  size={buttonSize}
                  selected={isSelected}
                  disabled={typeof onSelected !== 'function' || disabled}
                  key={tag}
                  onSelected={() => handleSelectClick(tag as TileValue<SchemaType>)}
                  tag={tag}
                  data-qa-id={`selection-tag-btn-${tag}`}
                />
              );
            })}
          </FlexRow>
          <FlexRow gap="2" wrap="nowrap" justifyContent="center" m="auto" data-testid="tags-row-2">
            {TAGS_TO_USE.slice(4).map((tag) => {
              const isSelected = selectedValue === tag;

              return (
                <SelectionTagButton
                  size={buttonSize}
                  selected={isSelected}
                  disabled={typeof onSelected !== 'function' || disabled}
                  key={tag}
                  onSelected={() => handleSelectClick(tag as TileValue<SchemaType>)}
                  tag={tag}
                  data-qa-id={`selection-tag-btn-${tag}`}
                />
              );
            })}
          </FlexRow>
        </FlexCol>
      </Dialog>
    </FlexRow>
  );
};
